import { Controller } from "@hotwired/stimulus";
import { emitCheckoutEvent } from "../../services/checkoutEvents";
import { debugLog } from "../../utils";

export default class extends Controller {
  static targets = ["text", "icon"];
  static values = {
    text: String,
    successMessage: { type: String, default: "Copied!" },
    errorMessage: { type: String, default: "Failed to copy" },
    // Opt-in analytics: when set, emit this event via emitCheckoutEvent when copy is successful. Since this controller
    // is reused across the checkout — keeping the emit gated by this value
    // avoids accidental funnel events on unrelated copy buttons.
    analyticsEvent: String,
  };

  connect() {
    debugLog("Copy Controller Connected");
  }

  async copy() {
    try {
      // Get the text to copy (either from value or target)
      let textToCopy = this.hasTextValue ? this.textValue : this.textTarget.textContent;

      // Use the modern Clipboard API
      await navigator.clipboard.writeText(textToCopy);

      // Show success feedback
      this.showSuccess();
      this.emitAnalytics();

      debugLog("Text copied successfully:", textToCopy);
    } catch (error) {
      // Fallback for older browsers or if clipboard API fails
      this.fallbackCopy();
    }
  }

  emitAnalytics() {
    if (!this.analyticsEventValue) return;
    emitCheckoutEvent(this.analyticsEventValue, { copy_method: "clipboard" });
  }

  showSuccess() {
    // Change icon to checkmark temporarily
    if (this.hasIconTarget) {
      const originalHTML = this.iconTarget.innerHTML;

      // Change to checkmark icon
      this.iconTarget.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
          <path d="M7.5 10L9.5 12L13.5 8" stroke="rgb(var(--primary-color))" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      // Add success class for styling
      this.element.classList.add('copy-success');

      // Revert back after 2 seconds
      setTimeout(() => {
        this.iconTarget.innerHTML = originalHTML;
        this.element.classList.remove('copy-success');
      }, 2000);
    }
  }

  fallbackCopy() {
    // Fallback method for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = this.hasTextValue ? this.textValue : this.textTarget.textContent;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      this.showSuccess();
      this.emitAnalytics();
      debugLog("Text copied using fallback method");
    } catch (error) {
      debugLog("Copy failed:", error);
      this.showError();
    } finally {
      document.body.removeChild(textArea);
    }
  }

  showError() {
    // Show error feedback
    if (this.hasIconTarget) {
      const originalHTML = this.iconTarget.innerHTML;

      // Change to error icon
      this.iconTarget.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
          <path d="M10 7V10M10 13H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="red" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;

      // Add error class for styling
      this.element.classList.add('copy-error');

      // Revert back after 2 seconds
      setTimeout(() => {
        this.iconTarget.innerHTML = originalHTML;
        this.element.classList.remove('copy-error');
      }, 2000);
    }
  }

  disconnect() {
    debugLog("Copy Controller Disconnected");
  }
}